import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { AuditFormData } from '@/pages/audits/CreateAudit';
import { UserAvatar } from '@/components/audits/UserAvatar';
import { Users, Mail, UserCheck, UserPlus, AlertCircle } from 'lucide-react';
import { useTeams } from '@/hooks/use-teams';
import { useUsers } from '@/hooks/use-users';
import { getIconByName } from '@/lib/team-icons';

// Helper function to check if a user belongs to a team
function isUserInTeam(user: { teams?: Array<string | { teamId: string }> }, teamId: string): boolean {
  if (!Array.isArray(user.teams)) return false;
  return user.teams.some(t => typeof t === 'string' ? t === teamId : t.teamId === teamId);
}

// Helper function to get users belonging to a specific team
function getTeamMembers<T extends { id: string; teams?: Array<string | { teamId: string }> }>(
  users: T[],
  teamId: string
): T[] {
  return users.filter(user => isUserInTeam(user, teamId));
}

// Helper function to get selection summary text
function getSelectionSummaryText(
  selectionMode: 'teams' | 'users',
  selectedTeamIds: string[],
  assigneeIds: string[]
): string {
  if (selectionMode === 'teams') {
    const teamCount = selectedTeamIds.length;
    const memberCount = assigneeIds.length;
    return `${teamCount} ${teamCount === 1 ? 'team' : 'teams'} with ${memberCount} ${memberCount === 1 ? 'member' : 'members'}`;
  }
  const auditorCount = assigneeIds.length;
  return `${auditorCount} ${auditorCount === 1 ? 'auditor' : 'auditors'} assigned to this audit`;
}

interface AssignTeamStepProps {
  formData: AuditFormData;
  updateFormData: (updates: Partial<AuditFormData>) => void;
}

type SelectionMode = 'teams' | 'users';

export function AssignTeamStep({ formData, updateFormData }: Readonly<AssignTeamStepProps>) {
  const [loading, setLoading] = useState(true);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('users');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(formData.selectedTeamIds || []);
  
  const teamsHook = useTeams();
  const usersHook = useUsers();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load teams and users from hooks
      await Promise.all([
        teamsHook.loadTeams(),
        usersHook.loadUsers(),
      ]);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (userId: string) => {
    const updated = formData.assigneeIds.includes(userId)
      ? formData.assigneeIds.filter(id => id !== userId)
      : [...formData.assigneeIds, userId];
    updateFormData({ assigneeIds: updated });
  };

  const toggleTeam = (teamId: string) => {
    const updated = selectedTeamIds.includes(teamId)
      ? selectedTeamIds.filter(id => id !== teamId)
      : [...selectedTeamIds, teamId];
    setSelectedTeamIds(updated);
    
    // Update assigneeIds based on selected teams
    const teamUserIds = new Set<string>();
    updated.forEach(tId => {
      const teamUsers = getTeamMembers(usersHook.users, tId);
      teamUsers.forEach(user => teamUserIds.add(user.id));
    });
    
    updateFormData({ assigneeIds: Array.from(teamUserIds), selectedTeamIds: updated });
  };

  const selectAll = () => {
    if (selectionMode === 'users') {
      updateFormData({ assigneeIds: usersHook.users.map(u => u.id), selectedTeamIds: [] });
    } else {
      const allTeamIds = teamsHook.teams.map(t => t.id);
      setSelectedTeamIds(allTeamIds);
      
      // Get all users from all teams
      const allUserIds = new Set<string>();
      usersHook.users.forEach(user => {
        if (Array.isArray(user.teams) && user.teams.length > 0) {
          allUserIds.add(user.id);
        }
      });
      updateFormData({ assigneeIds: Array.from(allUserIds), selectedTeamIds: allTeamIds });
    }
  };

  const clearAll = () => {
    updateFormData({ assigneeIds: [], selectedTeamIds: [] });
    setSelectedTeamIds([]);
  };

  const handleModeChange = (mode: SelectionMode) => {
    setSelectionMode(mode);
    // Clear selections when switching modes
    clearAll();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <>
      {/* Assign Team Card */}
      <Card className="bg-card border rounded-lg shadow-sm">
        <CardHeader>
          <CardTitle>Assign Team</CardTitle>
          <CardDescription>
            Select teams or individual users who will be responsible for conducting this audit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Selection Mode Toggle */}
          <div className="space-y-3">
            <Label>Selection Mode</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={selectionMode === 'teams' ? 'default' : 'outline'}
                onClick={() => handleModeChange('teams')}
                className="flex-1"
              >
                <Users className="h-4 w-4 mr-2" />
                Select by Team
              </Button>
              <Button
                type="button"
                variant={selectionMode === 'users' ? 'default' : 'outline'}
                onClick={() => handleModeChange('users')}
                className="flex-1"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Select Individual Users
              </Button>
            </div>
          </div>

          {/* Team/User Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>
                {selectionMode === 'teams' ? 'Select Teams' : 'Select Auditors'} <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-xs text-primary hover:underline"
                >
                  Select All
                </button>
                <span className="text-xs text-muted-foreground">|</span>
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs text-muted-foreground hover:underline"
                >
                  Clear All
                </button>
              </div>
            </div>

            {selectionMode === 'teams' ? (
              // Team Selection
              <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                {teamsHook.teams.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <AlertCircle className="h-12 w-12 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">No teams available</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Please create teams in the Teams & Permissions settings
                    </p>
                  </div>
                ) : (
                  teamsHook.teams.map((team) => {
                    const isSelected = selectedTeamIds.includes(team.id);
                    const teamMemberCount = getTeamMembers(usersHook.users, team.id).length;
                    
                    // Get the icon component from the icon name
                    const IconComponent = getIconByName(team.icon);
                    
                    return (
                      <div
                        key={team.id}
                        className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors"
                      >
                        <Checkbox
                          id={`team-${team.id}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleTeam(team.id)}
                        />
                        <label
                          htmlFor={`team-${team.id}`}
                          className="flex items-center gap-3 flex-1 cursor-pointer"
                        >
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: team.color }}
                          >
                            {IconComponent ? (
                              <IconComponent className="h-5 w-5 text-white" />
                            ) : (
                              <Users className="h-5 w-5 text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{team.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {teamMemberCount} {teamMemberCount === 1 ? 'member' : 'members'}
                            </p>
                          </div>
                          {team.isDefault && (
                            <Badge variant="secondary" className="text-xs">
                              Default
                            </Badge>
                          )}
                        </label>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              // User Selection
              <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                {usersHook.users.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <AlertCircle className="h-12 w-12 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">No users available</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Please add users to the system to assign them to audits
                    </p>
                  </div>
                ) : (
                  usersHook.users.map((user) => {
                    const isSelected = formData.assigneeIds.includes(user.id);
                    return (
                      <div
                        key={user.id}
                        className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors"
                      >
                        <Checkbox
                          id={`user-${user.id}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleUser(user.id)}
                        />
                        <label
                          htmlFor={`user-${user.id}`}
                          className="flex items-center gap-3 flex-1 cursor-pointer"
                        >
                          <UserAvatar user={{ id: user.id, name: user.name, email: user.email }} size="md" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">{user.name}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                          {user.role && (
                            <Badge variant="secondary" className="text-xs">
                              {user.role}
                            </Badge>
                          )}
                        </label>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {formData.assigneeIds.length === 0 && (
              <p className="text-sm text-orange-600 flex items-center gap-2">
                <UserCheck className="h-4 w-4" />
                Please select at least one {selectionMode === 'teams' ? 'team' : 'auditor'}
              </p>
            )}
          </div>

          {/* Team Summary */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-medium">
                  {selectionMode === 'teams' ? 'Selected Teams & Members' : 'Selected Auditors'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {getSelectionSummaryText(selectionMode, selectedTeamIds, formData.assigneeIds)}
                </p>
              </div>
              <Badge variant="secondary" className="text-lg px-4 py-2">
                {formData.assigneeIds.length}
              </Badge>
            </div>
            
            {selectionMode === 'teams' && selectedTeamIds.length > 0 && (
              <div className="space-y-3">
                {selectedTeamIds.map((teamId) => {
                  const team = teamsHook.teams.find(t => t.id === teamId);
                  if (!team) return null;
                  
                  const teamMembers = getTeamMembers(usersHook.users, teamId);
                  
                  // Get the icon component from the icon name
                  const IconComponent = getIconByName(team.icon);
                  
                  return (
                    <div key={teamId} className="bg-background rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div 
                          className="w-6 h-6 rounded flex items-center justify-center"
                          style={{ backgroundColor: team.color }}
                        >
                          {IconComponent ? (
                            <IconComponent className="h-3.5 w-3.5 text-white" />
                          ) : (
                            <Users className="h-3.5 w-3.5 text-white" />
                          )}
                        </div>
                        <span className="text-sm font-medium">{team.name}</span>
                        <Badge variant="outline" className="text-xs ml-auto">
                          {teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-2 ml-8">
                        {teamMembers.map((user) => (
                          <div key={user.id} className="flex items-center gap-1.5 bg-muted rounded-full pl-1 pr-2 py-0.5">
                            <UserAvatar user={{ id: user.id, name: user.name, email: user.email }} size="xs" />
                            <span className="text-xs">{user.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {selectionMode === 'users' && formData.assigneeIds.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.assigneeIds.map((userId) => {
                  const user = usersHook.users.find(u => u.id === userId);
                  return user ? (
                    <div key={userId} className="flex items-center gap-2 bg-background rounded-full pl-1 pr-3 py-1">
                      <UserAvatar user={{ id: user.id, name: user.name, email: user.email }} size="xs" />
                      <span className="text-xs font-medium">{user.name}</span>
                    </div>
                  ) : null;
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings Card */}
      <Card className="bg-card border rounded-lg shadow-sm">
        <CardHeader>
          <CardTitle>Notification Settings</CardTitle>
          <CardDescription>
            Configure how team members are notified about this audit
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="notifyAssignees" className="font-normal">
                  Notify assignees
                </Label>
              </div>
              <p className="text-xs text-muted-foreground ml-6">
                Send email notification when audit is created
              </p>
            </div>
            <Switch
              id="notifyAssignees"
              checked={formData.notifyAssignees}
              onCheckedChange={(checked) => updateFormData({ notifyAssignees: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="notifyOnCompletion" className="font-normal">
                  Notify on completion
                </Label>
              </div>
              <p className="text-xs text-muted-foreground ml-6">
                Send notification when audit is completed
              </p>
            </div>
            <Switch
              id="notifyOnCompletion"
              checked={formData.notifyOnCompletion}
              onCheckedChange={(checked) => updateFormData({ notifyOnCompletion: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="allowReassignment" className="font-normal">
                  Allow reassignment
                </Label>
              </div>
              <p className="text-xs text-muted-foreground ml-6">
                Auditors can reassign assets to other team members
              </p>
            </div>
            <Switch
              id="allowReassignment"
              checked={formData.allowReassignment}
              onCheckedChange={(checked) => updateFormData({ allowReassignment: checked })}
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
}
